import { ReactElement, useEffect, useState, useRef, useCallback } from "react";
import ReCAPTCHA from 'react-google-recaptcha'
import Select from "react-select"; 
import useApiState, { api } from "../../hooks/use-api-state";
import { IAppInfo } from "../../messages/IAppInfo";
import Loading from "../common/Loading";
import styles from "./FAR.module.css";
import "../common/General.module.css";
import AutoGrowInput from "../common/AutoGrowInput";
import { IFarUserData } from "../../messages/IFar";
import ErrorNotification, { SuccessNotification } from "../common/ErrorNotification";

export default function FAR(props: {
  appInfo: IAppInfo
}): ReactElement {
  const [apiState, setApiState] = useApiState(false);
  const [errorMessage, setErrorMessage] = useState<string|null>(null);
  const [successMessage, setSuccessMessage] = useState<string|null>(null);
  const [submitApiState, setSubmitApiState] = useApiState(false);
  const [token, setToken] = useState<string|null>(null);

  const [iskolaiVegzettsegOptions, setIskolaiVegzettsegOptions] = useState<string[]>([]);
  const [szuletesiOrszagOptions, setSzuletesiOrszagOptions] = useState<string[]>([]);

  const [email, setEmail] = useState("");
  const [fullname, setFullname] = useState("");
  const [szuletesiNeve, setSzuletesiNeve] = useState("");
  const [anyjaNeve, setAnyjaNeve] = useState("");
  const [szuletesiOrszag, setSzuletesiOrszag] = useState<string | null>("");
  const [szuletesiHelye, setSzuletesiHelye] = useState("");
  const [szuletesiIdeje, setSzuletesiIdeje] = useState("");
  const [iskolaiVegzettsege, setIskolaiVegzettsege] = useState("");
  const [nemMagyarAllampolgar, setNemMagyarAllampolgar] = useState(false);

  const [captchaOK, setCaptchaOK] = useState(false);
  const recaptcha = useRef<ReCAPTCHA | null>(null);

  let fetchCallback = useCallback(async function (token: string) {
    try {
      let ret = await api.getFarUser(setApiState, token);
      setEmail(ret.email ?? "");
      setFullname(ret.fullname ?? "");
      setSzuletesiNeve(ret.szuletesiNeve ?? "");
      setAnyjaNeve(ret.anyjaNeve ?? "");
      setSzuletesiOrszag(ret.szuletesiOrszag ?? null);
      setSzuletesiHelye(ret.szuletesiHelye ?? "");
      setSzuletesiIdeje(ret.szuletesiIdeje ?? "");
      setIskolaiVegzettsege(ret.iskolaiVegzettsege ?? "");
      setNemMagyarAllampolgar(ret.nemMagyarAllampolgar ?? false);

      setIskolaiVegzettsegOptions(ret.far_data?.iskolai_vegzettseg ?? []);
      setSzuletesiOrszagOptions(["Magyarország", ...(ret.far_data?.szuletesi_orszagok ?? []).filter(orszag => orszag !== "Magyarország")]);
    } catch (e: any) {
      setErrorMessage("Error fetching FAR user data")
      console.error("Error fetching FAR user data:", e);
    }
  }, [setApiState]);


  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      setToken(token);
      fetchCallback(token);
    }
    else {
      setErrorMessage("No token provided")
    }
  }, [fetchCallback]);


  async function submitForm() {
    const formData : IFarUserData = {
      email,
      fullname,
      szuletesiNeve,
      anyjaNeve,
      szuletesiOrszag,
      szuletesiHelye,
      szuletesiIdeje,
      iskolaiVegzettsege,
      nemMagyarAllampolgar,
    };
    const recaptchaToken = recaptcha.current?.getValue() ?? null;
    if (token) {
      let ret = await api.saveFarUser(setSubmitApiState, token, formData, recaptchaToken);
      //console.log(`Save FAR user response: status=${ret.status}, message=${ret.message}`);
      if (ret.status==="ok") {
        setSuccessMessage("Adatok elmentve. Köszönjük!")
      }
      else {
        let message = "Hiba történt az adatok mentése során";
        if (ret.message)
          message += `: ${ret.message}`;
        setErrorMessage(message);
      }
    }
  }

  function recaptchaChanged(token: string|null) {
      setCaptchaOK(!!recaptcha.current?.getValue());
  };

  return (
    <>
      <div className={styles.introText}>
        <h1>Tisztelt résztvevő</h1>
        <p>Az ön részletes személyes adatainak megadása a felnőttképzési jogszabályok alapján kötelező (2013. évi LXXVII. törvény), 
          amit a képzés megvalósítása és a Felnőttképzési Adatszolgáltatási Rendszer (FAR) felé történő adatszolgáltatás céljából kérünk be.</p>
        <p>Az adatok kezelésére és továbbítására vonatkozó részletes tájékoztatást a FAR hivatalos adatkezelési tájékoztatójában találja meg a <a href="https://far.nive.hu" target="_blank" rel="noopener noreferrer">https://far.nive.hu</a> oldalon 
        (az <i>Adatkezelési tájékoztató</i> az oldal alján érhető el).</p>
      </div>

      {!!token &&
        <Loading isLoading={apiState.isLoading}>
          <form className={styles.form} onSubmit={e => { e.preventDefault(); submitForm(); }}>

            <AutoGrowInput type="email" name="emailCime"
              label="E-mail címe"
              value={email}
              disabled={true}
              /*onChange={(e) => setEmail(e.target.value)}*/
            />

            <AutoGrowInput type="text" name="viseltNeve"
              className={styles.viseltNeve}
              label="Viselt neve"
              value={fullname}
              disabled={true}
              /*onChange={(e) => setFullname(e.target.value)}*/
            />

            <div className={`grid-spacer ${styles.gridSpacer}`}></div>
            
            <AutoGrowInput type="text" name="szuletesiNeve"
              label="Születési neve"
              placeholder={fullname}
              value={szuletesiNeve}
              onChange={(e) => setSzuletesiNeve(e.target.value)}
              title="Csak akkor kell kitölteni, ha különbözik a viselt névtől"
            />

            <AutoGrowInput type="text" name="anyjaNeve"
              label="Anyja neve"
              value={anyjaNeve}
              onChange={(e) => setAnyjaNeve(e.target.value)}
            />

            {/*
            <AutoGrowInput type="text" name="szuletesiOrszag"
              label="Születési ország"
              placeholder="Magyarország"
              value={szuletesiOrszag}
              onChange={(e) => setSzuletesiOrszag(e.target.value)}
              title="Az országot csak akkor kell kitölteni, ha az nem Magyarország"
            />
            */}

            <span className={styles.selectNoteSpan}>Az országot csak akkor kell kitölteni, ha az nem Magyarország</span>

            <label htmlFor="szuletesiOrszag">Születési ország:</label>
            <Select
              inputId="szuletesiOrszag"
              className={`FARSelector ${styles.szuletesiOrszag}`}
              classNamePrefix="FARSelector"
              placeholder="Magyarország"
              options={szuletesiOrszagOptions.map((orszag: string, index) => {return {"label": orszag, "value": index};})}
              onChange={(e) => setSzuletesiOrszag(e?.label === "Magyarország" ? null : e?.label || "")}
              value={{"label": szuletesiOrszag, "value": szuletesiOrszagOptions.findIndex((orszag: string) => orszag === szuletesiOrszag)}}
              defaultValue={{"label": "", "value": 0}}
            />

            <AutoGrowInput type="text" name="szuletesiHelye"
              label="Születési helye"
              value={szuletesiHelye}
              onChange={(e) => setSzuletesiHelye(e.target.value)}
            />

            <label htmlFor="szuletesiIdeje">Születési ideje:</label>
            <input
              id="szuletesiIdeje"
              type="text"
              name="szuletesiIdeje"
              placeholder="yyyy.mm.dd"
              pattern="\d{4}\.\d{2}\.\d{2}"
              inputMode="numeric"
              style={{ width: "150px" }}
              value={szuletesiIdeje}
              onChange={(e) => setSzuletesiIdeje(e.target.value)}
            />

            {/*
            <AutoGrowInput type="text" name="iskolaiVegzettsege"
              label="Legmagasabb iskolai végzettsége"
              value={iskolaiVegzettsege}
              onChange={(e) => setIskolaiVegzettsege(e.target.value)}
            />
            */}

            <label htmlFor="iskolaiVegzettsege">Legmagasabb iskolai végzettsége:</label>
            <Select
              inputId="iskolaiVegzettsege"
              className={`FARSelector ${styles.iskolaiVegzettsege}`}
              classNamePrefix="FARSelector"
              options={iskolaiVegzettsegOptions.map((orszag: string, index) => {return {"label": orszag, "value": index};})}
              onChange={(e) => setIskolaiVegzettsege(e?.label || "")}
              value={{"label": iskolaiVegzettsege, "value": iskolaiVegzettsegOptions.findIndex((orszag: string) => orszag === iskolaiVegzettsege)}}
              defaultValue={{"label": "Magyarország", "value": 0}}
              //menuIsOpen={true}
            />

            <label htmlFor="nemMagyarAllampolgar" title="Itt kell jelezni, ha a képzésben részt vevő személy olyan nem magyar állampolgár, aki Magyarországon nem rendelkezik lakcímmel (lakóhellyel vagy tartózkodási hellyel).">
              Magyarországi lakcímmel nem rendelkező<br/>nem magyar állampolgár
            </label>
            <input type="checkbox" name="nemMagyarAllampolgar" id="nemMagyarAllampolgar"
              checked={nemMagyarAllampolgar}
              onChange={(e) => setNemMagyarAllampolgar(e.target.checked)}
            />

          </form>
        </Loading>
      }


      {!!token &&
        <div className={styles.recaptcha}>
          <ReCAPTCHA ref={recaptcha} sitekey={props.appInfo.captcha_sitekey} onChange={recaptchaChanged}/>
        </div>
      }

      {(!!errorMessage || !!successMessage) && (  
        <div className={styles.notificationDiv}>
        {!!errorMessage  ? (
          <ErrorNotification  errorMessage={errorMessage} />
        ) : 
        !!successMessage && (
          <SuccessNotification message={successMessage} />
        )}
        </div>
      )}

      {!!token &&
        <Loading isLoading={submitApiState.isLoading}>
            <div className={styles.submitButtonContainer}>
              <button className={`greenButton`}
                  onClick={() => submitForm()}
                  disabled={!captchaOK}
              >Elküld</button>
            </div>
        </Loading>
      }

    </>
  );
}
